/**
 * Remembers which teacher a student was looking at when they bought or claimed
 * a lesson, so booking that course can start with that teacher already chosen.
 * Scoped to the course: buying something else later must not reuse the hint.
 *
 * Lives in sessionStorage: it survives the login round trip and the ECPay
 * redirect in the same tab, which is where almost every post-purchase booking
 * happens. It is a default, not a binding — the student can still switch.
 *
 * Every function is safe to call when storage is unavailable — private windows
 * and blocked site data make the accessor throw.
 */

const STORAGE_KEY = 'preferredTeacherUserId'

function storage() {
  try {
    return globalThis.sessionStorage || null
  } catch {
    return null
  }
}

/**
 * @param {number} teacherId the teacher's user id
 * @param {number} courseId the team course being bought
 */
export function rememberPreferredTeacher(teacherId, courseId) {
  const store = storage()
  if (!store || teacherId == null || courseId == null) return

  try {
    store.setItem(STORAGE_KEY, JSON.stringify({ teacherId: Number(teacherId), courseId: Number(courseId) }))
  } catch {
    // Losing the hint only costs the student one extra tap.
  }
}

/**
 * Returns the remembered teacher when the hint is for this course and they can
 * be booked. Leaves the hint in place either way, so opening another course
 * first does not lose it.
 *
 * @param {Array<{id: number, has_schedule?: boolean}>} teachers the course's teachers
 * @param {number} courseId the course being booked
 * @returns {Object|null}
 */
export function findPreferredTeacher(teachers, courseId) {
  const store = storage()
  if (!store || !Array.isArray(teachers)) return null

  let raw
  try {
    raw = store.getItem(STORAGE_KEY)
  } catch {
    return null
  }
  if (!raw) return null

  let hint
  try {
    hint = JSON.parse(raw)
  } catch {
    return null
  }
  if (!hint || typeof hint !== 'object' || hint.courseId !== Number(courseId)) return null

  const teacher = teachers.find(t => t.id === hint.teacherId)
  // A teacher with no schedule would land the student on an empty calendar.
  if (!teacher || teacher.has_schedule === false) return null
  return teacher
}

/** Call after a booking succeeds: the hint has done its job. */
export function clearPreferredTeacher() {
  const store = storage()
  if (!store) return

  try {
    store.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to do: sessionStorage ends with the tab.
  }
}
